import React from 'react';
import classNames from 'classnames';
import BenefitList, { BenefitSection } from '../benefit-list';
import MeidouPanel from '../meidou-panel';

import styles from './index.module.css';
import IconFont from '@/components/icon-font';

export type CardTier = 'free' | 'basic' | 'pro';

interface PricingCardProps {
  /** 卡片层级: free / basic / pro */
  tier: CardTier;
  /** 版本名称 */
  title: string;
  /** 价格文字 如 "¥0" */
  price: string;
  /** 价格单位 如 "/月" */
  priceUnit?: string;
  /** 价格数字旁补充文案，如低价试用「试用7天」 */
  priceDesc?: string;
  /** 商品中台配置的横幅文案 */
  productDesc?: string;
  /** 中台配置的续期商品文案 */
  explain?: string;
  /** 点击问号图标：打开服务须知等弹窗 */
  onExplainIconClick?: () => void;
  /** 按钮文案 */
  buttonText: string;
  /** 按钮是否禁用（免费版已解锁） */
  buttonDisabled?: boolean;
  /** 按钮点击 */
  onButtonClick?: () => void;
  /** 美豆面板配置 */
  meidouPanel?: {
    meidouText: string;
    resourceDesc?: string;
    unitPrice?: string;
    tags?: string[];
    expandable?: boolean;
  };
  /** 权益列表 */
  benefits?: BenefitSection[];
  /** 角标文案，如 "最受喜欢" */
  badge?: string;
  /** 是否选中/高亮 */
  highlighted?: boolean;
  /** 加载占位：与 meidou-products 骨架屏同逻辑，保留布局、隐藏内容并禁止交互 */
  skeleton?: boolean;
}

const PricingCard: React.FC<PricingCardProps> = ({
  tier,
  title,
  price,
  priceUnit = '/月',
  priceDesc,
  explain,
  onExplainIconClick,
  buttonText,
  buttonDisabled = false,
  onButtonClick,
  meidouPanel,
  benefits = [],
  badge,
  highlighted = false,
  productDesc,
  skeleton = false,
}) => {
  return (
    <div
      className={classNames(styles.card, {
        [styles.cardHighlighted]: highlighted && !skeleton,
        [styles.cardFree]: tier === 'free',
        [styles.cardBasic]: tier === 'basic',
        [styles.cardPro]: tier === 'pro',
        [styles.skeleton]: skeleton,
      })}
    >
      {/* 角标 */}
      {badge && (
        <div className={styles.badge}>
          <span className={styles.badgeText}>{badge}</span>
        </div>
      )}

      <div className={styles.cardInner}>
        {/* 版本名称 + 平台 */}
        <div className={styles.titleSection}>
          <h3 className={styles.tierTitle}>{title}</h3>
        </div>

        {/* 价格 */}
        <div className={styles.priceSection}>
          <div className={styles.priceRow}>
            <div className={styles.priceValueContainer}>
              <span className={styles.priceValue}>{price}</span>
              {priceDesc ? (
                <span className={styles.priceDesc}>{priceDesc}</span>
              ) : (
                <span className={styles.priceUnit}>{priceUnit}</span>
              )}
            </div>

            {productDesc ? (
              <div className={styles.productDescContainer}>
                <span className={styles.productDesc}>{productDesc}</span>
              </div>
            ) : null}
          </div>
          {explain && (
            <p className={styles.explain}>
              {explain}
              <span className={styles.explainIconWrap} onClick={onExplainIconClick}>
                <IconFont className={styles.explainIcon} name="questionmarkCircle" fontSize={18} />
              </span>
            </p>
          )}
        </div>

        {/* 订阅按钮 */}
        <div className={styles.buttonSection}>
          <button
            className={classNames(styles.subscribeBtn, {
              [styles.subscribeBtnDisabled]: buttonDisabled,
              [styles.subscribeBtnActive]: !buttonDisabled && tier === 'basic',
              [styles.proSubscribeBtnActive]: !buttonDisabled && tier === 'pro',
            })}
            disabled={buttonDisabled}
            onClick={onButtonClick}
          >
            {!buttonDisabled && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className={styles.vipIcon}
              >
                <path
                  d="M4.59133 1.50001H11.4184C11.632 1.49955 11.8432 1.54495 12.0377 1.63314C12.2322 1.72133 12.4056 1.85025 12.546 2.01119L15.3324 5.22739C15.5682 5.49813 15.6981 5.84502 15.6981 6.20402C15.6981 6.56303 15.5682 6.90991 15.3324 7.18066L9.12307 14.3222C8.98323 14.4827 8.81063 14.6114 8.61688 14.6995C8.42312 14.7877 8.21272 14.8333 7.99985 14.8333C7.78697 14.8333 7.57656 14.7877 7.38281 14.6995C7.18905 14.6114 7.01645 14.4827 6.87662 14.3222L0.665991 7.18066C0.43048 6.9098 0.300781 6.56295 0.300781 6.20402C0.300781 5.84509 0.43048 5.49825 0.665991 5.22739L3.46372 2.01119C3.60393 1.84999 3.77722 1.72089 3.9718 1.63268C4.16638 1.54447 4.37769 1.49922 4.59133 1.50001ZM5.83571 7.91982C6.45952 8.35825 7.20317 8.59403 7.96564 8.59514C8.69534 8.59299 9.40824 8.37598 10.0154 7.97119L11.5026 6.98516C11.667 6.87613 11.7814 6.70626 11.8207 6.51291C11.8599 6.31955 11.8207 6.11853 11.7118 5.95402C11.6579 5.87248 11.5885 5.80236 11.5075 5.74769C11.4265 5.69302 11.3354 5.65488 11.2397 5.63544C11.1439 5.61601 11.0452 5.61566 10.9493 5.63442C10.8533 5.65319 10.7621 5.69069 10.6807 5.74479L9.19348 6.73082C8.82123 6.97855 8.38285 7.1081 7.93574 7.10251C7.48863 7.09692 7.05362 6.95645 6.68768 6.6995L5.33455 5.75481C5.17272 5.64185 4.97265 5.5978 4.77834 5.63234C4.58403 5.66688 4.41139 5.77718 4.2984 5.93899C4.18739 6.10144 4.1445 6.30095 4.17894 6.49467C4.21337 6.68839 4.32239 6.86089 4.48258 6.97514L5.83571 7.91982Z"
                  fill="#55230B"
                />
              </svg>
            )}
            <span>{buttonText}</span>
          </button>
        </div>

        {/* 分割线 */}
        <div className={styles.divider} />

        {/* 美豆面板 */}
        {meidouPanel && <MeidouPanel {...meidouPanel} />}

        {/* 权益列表 */}
        {benefits.length > 0 && <BenefitList sections={benefits} />}
      </div>
    </div>
  );
};

export default React.memo(PricingCard);
